import { useReducer } from 'react';

const initialState = {
  isLoading: false,
  loadingMessage: '',
  isError: false,
  isOnHold: true,
  isFinalized: true
};

const ACTION_TYPES = {
  // place encounter on hold
  PLACE_ENCOUNTER_ON_HOLD_REQUEST: 'PLACE_ENCOUNTER_ON_HOLD_REQUEST',
  PLACE_ENCOUNTER_ON_HOLD_SUCCESS: 'PLACE_ENCOUNTER_ON_HOLD_SUCCESS',
  PLACE_ENCOUNTER_ON_HOLD_FAILURE: 'PLACE_ENCOUNTER_ON_HOLD_FAILURE',

  // remove encounter hold
  REMOVE_ENCOUNTER_ON_HOLD_REQUEST: 'REMOVE_ENCOUNTER_ON_HOLD_REQUEST',
  REMOVE_ENCOUNTER_ON_HOLD_SUCCESS: 'REMOVE_ENCOUNTER_ON_HOLD_SUCCESS',
  REMOVE_ENCOUNTER_ON_HOLD_FAILURE: 'REMOVE_ENCOUNTER_ON_HOLD_FAILURE',

  // exit encounter
  EXIT_ENCOUNTER_REQUEST: 'EXIT_ENCOUNTER_REQUEST',
  EXIT_ENCOUNTER_SUCCESS: 'EXIT_ENCOUNTER_SUCCESS',
  EXIT_ENCOUNTER_FAILURE: 'EXIT_ENCOUNTER_FAILURE',

  // finalize encounter
  FINALIZE_ENCOUNTER_REQUEST: 'FINALIZE_ENCOUNTER_REQUEST',
  FINALIZE_ENCOUNTER_SUCCESS: 'FINALIZE_ENCOUNTER_SUCCESS',
  FINALIZE_ENCOUNTER_FAILURE: 'FINALIZE_ENCOUNTER_FAILURE',

  // unfinalize encounter
  UNFINALIZE_ENCOUNTER_REQUEST: 'UNFINALIZE_ENCOUNTER_REQUEST',
  UNFINALIZE_ENCOUNTER_SUCCESS: 'UNFINALIZE_ENCOUNTER_SUCCESS',
  UNFINALIZE_ENCOUNTER_FAILURE: 'UNFINALIZE_ENCOUNTER_FAILURE',

  // complete encounter
  COMPLETE_ENCOUNTER_REQUEST: 'COMPLETE_ENCOUNTER_REQUEST',
  COMPLETE_ENCOUNTER_SUCCESS: 'COMPLETE_ENCOUNTER_SUCCESS',
  COMPLETE_ENCOUNTER_FAILURE: 'COMPLETE_ENCOUNTER_FAILURE',

  MARK_AS_INVALID_REQUEST: 'MARK_AS_INVALID_REQUEST',
  MARK_AS_INVALID_SUCCESS: 'MARK_AS_INVALID_SUCCESS',
  MARK_AS_INVALID_FAILURE: 'MARK_AS_INVALID_FAILURE',

  HYDRATE: 'HYDRATE'
};

const ACTIONS = {
  // place encounter on hold
  placeOnHoldRequest: () => ({
    type: ACTION_TYPES.PLACE_ENCOUNTER_ON_HOLD_REQUEST
  }),
  placeOnHoldSuccess: nextEncounterId => ({
    type: ACTION_TYPES.PLACE_ENCOUNTER_ON_HOLD_SUCCESS,
    payload: {
      nextEncounterId
    }
  }),
  placeOnHoldFailure: error => ({
    type: ACTION_TYPES.PLACE_ENCOUNTER_ON_HOLD_FAILURE,
    error
  }),

  // remove encounter hold
  removeFromHoldRequest: () => ({
    type: ACTION_TYPES.REMOVE_ENCOUNTER_ON_HOLD_REQUEST
  }),
  removeFromHoldSuccess: () => ({
    type: ACTION_TYPES.REMOVE_ENCOUNTER_ON_HOLD_SUCCESS
  }),
  removeFromHoldFailure: error => ({
    type: ACTION_TYPES.REMOVE_ENCOUNTER_ON_HOLD_FAILURE,
    error
  }),

  // exit encounter
  exitEncounterRequest: () => ({
    type: ACTION_TYPES.EXIT_ENCOUNTER_REQUEST
  }),
  exitEncounterSuccess: () => ({
    type: ACTION_TYPES.EXIT_ENCOUNTER_SUCCESS
  }),
  exitEncounterFailure: error => ({
    type: ACTION_TYPES.EXIT_ENCOUNTER_FAILURE,
    error
  }),

  // finalize encounter
  finalizeEncounterRequest: () => ({
    type: ACTION_TYPES.FINALIZE_ENCOUNTER_REQUEST
  }),
  finalizeEncounterSuccess: nextEncounterId => ({
    type: ACTION_TYPES.FINALIZE_ENCOUNTER_SUCCESS,
    payload: {
      nextEncounterId
    }
  }),
  finalizeEncounterFailure: error => ({
    type: ACTION_TYPES.FINALIZE_ENCOUNTER_FAILURE,
    error
  }),

  // unfinalize encounter
  unfinalizeEncounterRequest: () => ({
    type: ACTION_TYPES.UNFINALIZE_ENCOUNTER_REQUEST
  }),
  unfinalizeEncounterSuccess: () => ({
    type: ACTION_TYPES.UNFINALIZE_ENCOUNTER_SUCCESS
  }),
  unfinalizeEncounterFailure: error => ({
    type: ACTION_TYPES.UNFINALIZE_ENCOUNTER_FAILURE,
    error
  }),

  completeEncounterRequest: () => ({
    type: ACTION_TYPES.COMPLETE_ENCOUNTER_REQUEST
  }),
  completeEncounterSuccess: () => ({
    type: ACTION_TYPES.COMPLETE_ENCOUNTER_SUCCESS
  }),
  completeEncounterFailure: error => ({
    type: ACTION_TYPES.COMPLETE_ENCOUNTER_FAILURE,
    error
  }),

  markAsInvalidRequest: () => ({
    type: ACTION_TYPES.MARK_AS_INVALID_REQUEST
  }),
  markAsInvalidSuccess: () => ({
    type: ACTION_TYPES.MARK_AS_INVALID_SUCCESS
  }),
  markAsInvalidFailure: error => ({
    type: ACTION_TYPES.MARK_AS_INVALID_FAILURE,
    error
  }),

  hydrate: (isOnHold, isFinalized) => ({
    type: ACTION_TYPES.HYDRATE,
    payload: {
      isOnHold,
      isFinalized
    }
  })
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.MARK_AS_INVALID_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Marking encounter as invalid'
      };
    }

    case ACTION_TYPES.MARK_AS_INVALID_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.MARK_AS_INVALID_FAILURE: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.COMPLETE_ENCOUNTER_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Completing encounter'
      };
    }

    case ACTION_TYPES.COMPLETE_ENCOUNTER_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.COMPLETE_ENCOUNTER_FAILURE: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.PLACE_ENCOUNTER_ON_HOLD_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Placing hold on encounter'
      };
    }

    case ACTION_TYPES.PLACE_ENCOUNTER_ON_HOLD_SUCCESS: {
      return {
        ...state,
        isOnHold: true,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.PLACE_ENCOUNTER_ON_HOLD_FAILURE: {
      return { ...state, isLoading: false, loadingMessage: '' };
    }

    case ACTION_TYPES.REMOVE_ENCOUNTER_ON_HOLD_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Removing hold from encounter'
      };
    }

    case ACTION_TYPES.REMOVE_ENCOUNTER_ON_HOLD_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: '',
        isOnHold: false
      };
    }

    case ACTION_TYPES.REMOVE_ENCOUNTER_ON_HOLD_FAILURE: {
      return { ...state, isLoading: false, loadingMessage: '' };
    }

    case ACTION_TYPES.FINALIZE_ENCOUNTER_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Finalizing encounter'
      };
    }

    case ACTION_TYPES.FINALIZE_ENCOUNTER_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: '',
        isFinalized: true
      };
    }

    case ACTION_TYPES.FINALIZE_ENCOUNTER_FAILURE: {
      return { ...state, isLoading: false, loadingMessage: '' };
    }

    case ACTION_TYPES.UNFINALIZE_ENCOUNTER_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Unfinalizing encounter'
      };
    }

    case ACTION_TYPES.UNFINALIZE_ENCOUNTER_SUCCESS: {
      return {
        ...state,
        isFinalized: false,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.UNFINALIZE_ENCOUNTER_FAILURE: {
      return { ...state, isLoading: false, loadingMessage: '' };
    }

    case ACTION_TYPES.EXIT_ENCOUNTER_REQUEST: {
      return { ...state, isLoading: true, loadingMessage: 'Exiting encounter' };
    }

    case ACTION_TYPES.EXIT_ENCOUNTER_SUCCESS: {
      return { ...state, isLoading: false, loadingMessage: '' };
    }

    case ACTION_TYPES.EXIT_ENCOUNTER_FAILURE: {
      return { ...state, isLoading: false, loadingMessage: '' };
    }

    case ACTION_TYPES.HYDRATE: {
      const { payload: { isOnHold, isFinalized } } = action;
      return { ...state, isOnHold, isFinalized };
    }

    default: {
      throw Error('useWorkflowSection reducer should never hit default');
    }
  }
};

const useWorkflowSection = service => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const placeOnHold = (encounterId, holdReasonId, comment, doRedirect) => {
    dispatch(ACTIONS.placeOnHoldRequest());

    return service.placeOnHold(encounterId, holdReasonId, comment)
      .then(nextEncounterId => {
        dispatch(ACTIONS.placeOnHoldSuccess(nextEncounterId));

        if (nextEncounterId === 0 || nextEncounterId === undefined) {
          // redirect to coding queue filter
          doRedirect(service.serviceRoutes.redirectToFilter);
        } else {
          // redirect to details screen with nextEncounterId from server
          doRedirect(service.serviceRoutes.redirectToDetails(nextEncounterId));
        }
      })
      .catch(e => dispatch(ACTIONS.placeOnHoldFailure(e.toString())));
  };

  const removeFromHold = encounterId => {
    dispatch(ACTIONS.removeFromHoldRequest());

    return service.removeFromHold(encounterId)
      .then(() => {
        dispatch(ACTIONS.removeFromHoldSuccess());
      })
      .catch(e => dispatch(ACTIONS.removeFromHoldFailure(e.toString())));
  };

  const finalizeEncounter = (encounterId, doRedirect) => {
    dispatch(ACTIONS.finalizeEncounterRequest());

    return service.finalizeEncounter(encounterId)
      .then(nextEncounterId => {
        dispatch(ACTIONS.finalizeEncounterSuccess(nextEncounterId));

        if (nextEncounterId === 0 || nextEncounterId === undefined) {
          // redirect to coding queue filter
          doRedirect(service.serviceRoutes.redirectToFilter);
        } else {
          // redirect to details screen with nextEncounterId from server
          doRedirect(service.serviceRoutes.redirectToDetails(nextEncounterId));
        }
      })
      .catch(e => dispatch(ACTIONS.finalizeEncounterFailure(e.toString())));
  };

  const unfinalizeEncounter = encounterId => {
    dispatch(ACTIONS.unfinalizeEncounterRequest());

    return service.unfinalizeEncounter(encounterId)
      .then(() => dispatch(ACTIONS.unfinalizeEncounterSuccess()))
      .catch(e => dispatch(ACTIONS.unfinalizeEncounterFailure(e.toString())));
  };

  const exitEncounter = (encounterId, doRedirect) => {
    dispatch(ACTIONS.exitEncounterRequest());

    return service.exitEncounter(encounterId)
      .then(() => {
        dispatch(ACTIONS.exitEncounterSuccess());
        doRedirect(service.serviceRoutes.redirectToFilter);
      })
      .catch(e => dispatch(ACTIONS.exitEncounterFailure(e.toString())));
  };

  const completeEncounter = (encounterId, doRedirect) => {
    dispatch(ACTIONS.completeEncounterRequest());

    return service.completeEncounter(encounterId)
      .then(nextEncounterId => {
        dispatch(ACTIONS.completeEncounterSuccess());

        if (nextEncounterId === 0 || nextEncounterId === undefined) {
          doRedirect(service.serviceRoutes.redirectToFilter);
        } else {
          doRedirect(service.serviceRoutes.redirectToDetails(nextEncounterId));
        }
      })
      .catch(e => dispatch(ACTIONS.completeEncounterFailure(e.toString())));
  };

  const markEncounterAsInvalid = (encounterId, doRedirect) => {
    dispatch(ACTIONS.markAsInvalidRequest());

    return service.markEncounterAsInvalid(encounterId)
      .then(nextEncounterId => {
        dispatch(ACTIONS.markAsInvalidSuccess());

        if (nextEncounterId === 0 || nextEncounterId === undefined) {
          doRedirect(service.serviceRoutes.redirectToFilter);
        } else {
          doRedirect(service.serviceRoutes.redirectToDetails(nextEncounterId));
        }
      })
      .catch(e => dispatch(ACTIONS.markAsInvalidFailure(e.toString())));
  };

  const hydrate = (isOnHold, isFinalized) =>
    dispatch(ACTIONS.hydrate(isOnHold, isFinalized));

  const boundActions = {
    placeOnHold,
    removeFromHold,
    finalizeEncounter,
    unfinalizeEncounter,
    exitEncounter,
    completeEncounter,
    markEncounterAsInvalid,
    hydrate
  };

  return [state, boundActions];
};

export default useWorkflowSection;
